import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import { captureAcquisition } from "@/lib/attributionCapture";

// Capture traffic acquisition synchronously BEFORE React renders so the
// original landing source/medium/campaign/utm_term is preserved across all
// subsequent SPA navigation and form submissions. Must run before EstimateForm
// can mount and submit.
try {
  captureAcquisition();
} catch (e) {
  console.warn('[Attribution] capture failed', e);
}

// Global safety net: cap any Supabase/network request at 25s so hung
// requests surface as errors instead of infinite spinners across admin views
// (CRM, Settings, Staff, Calendar, etc). React Query's retry then kicks in.
try {
  const SUPABASE_HOST = "pduieuyzixdbhmwaymlo.supabase.co";
  const originalFetch = window.fetch.bind(window);
  window.fetch = ((input: RequestInfo | URL, init?: RequestInit) => {
    let url = "";
    try {
      url = typeof input === "string" ? input : input instanceof URL ? input.href : (input as Request).url;
    } catch { /* ignore */ }
    if (!url.includes(SUPABASE_HOST)) return originalFetch(input, init);
    if (init?.signal?.aborted) return originalFetch(input, init);

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(new DOMException("Request timed out", "TimeoutError")), 25000);
    if (init?.signal) {
      if (init.signal.aborted) controller.abort();
      else init.signal.addEventListener("abort", () => controller.abort(), { once: true });
    }
    return originalFetch(input, { ...init, signal: controller.signal }).finally(() => clearTimeout(timer));
  }) as typeof window.fetch;
} catch (e) {
  console.warn("[fetch-timeout] setup failed", e);
}

// Block Google Analytics for admin pages and Lovable dev environments BEFORE React loads
const isDevEnv = () => {
  const hostname = window.location.hostname;
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    (hostname.includes('-preview--') && hostname.endsWith('.lovable.app')) ||
    (hostname.endsWith('.lovable.app') && hostname !== 'lt-vibe.lovable.app')
  );
};

if (window.location.pathname.startsWith('/admin') || isDevEnv()) {
  // Disable GA tracking by preventing gtag from sending data
  window.gtag = function() {
    // No-op - block all GA calls
  };
}

// Render React FIRST - Sentry initializes after idle
createRoot(document.getElementById("root")!).render(<App />);

// Signal to the build-time prerenderer that the page is ready to snapshot.
// Fired after the next two animation frames so React + SEOHead useLayoutEffect
// have written meta tags and JSON-LD schema into <head>. No-op at runtime.
if (typeof window !== "undefined") {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      document.dispatchEvent(new Event("render-event"));
    });
  });
}

// Defer Sentry initialization until after first paint (non-blocking)
const initSentryDeferred = () => {
  import("@/lib/sentry").then(({ initSentry }) => {
    initSentry();
  });
};

// Use requestIdleCallback with setTimeout fallback for maximum compatibility
if ('requestIdleCallback' in window) {
  (window as any).requestIdleCallback(initSentryDeferred, { timeout: 3000 });
} else {
  setTimeout(initSentryDeferred, 2000);
}
